import React, { useState, useEffect, useRef, useCallback } from "react";

// ── Default Knowledge Base ─────────────────────────────────────────────────
const DEFAULT_CATEGORIES = [
  {
    id: "introduction",
    label: "Introduction",
    icon: "◈",
    color: "#0071e3",
  },
  {
    id: "technology",
    label: "Technology",
    icon: "⬡",
    color: "#4ECDC4",
  },
  {
    id: "revenue",
    label: "Revenue & Commercial",
    icon: "◉",
    color: "#A8E063",
  },
  {
    id: "territory",
    label: "Territory & Licensing",
    icon: "◫",
    color: "#C9A0DC",
  },
  {
    id: "finance",
    label: "Finance & PPP",
    icon: "⬕",
    color: "#FF6B6B",
  },
  {
    id: "community",
    label: "Community Tips",
    icon: "✦",
    color: "#FFD93D",
  },
];

const DEFAULT_QA = [
  // INTRODUCTION
  {
    id: "q001",
    category: "introduction",
    question: "What is iLamp?",
    answer: `iLamp is an autonomous, solar-powered smart street lighting system developed by Conflow Power Group Limited (CPG). It is far more than a streetlight — it is a self-contained infrastructure node that generates its own energy, illuminates public spaces, and simultaneously runs advanced edge AI compute through an embedded NVIDIA Jetson Orin module.\n\nEvery iLamp unit is essentially a small AI data centre mounted on a pole. The combination of renewable energy generation, LED lighting, and high-performance compute creates a platform capable of delivering multiple revenue streams from a single piece of street infrastructure — without drawing power from the grid.\n\nThe iLamp platform is deployed through a territorial licensing model, enabling licence holders to develop, own, and operate iLamp infrastructure within defined geographic territories under commercial agreements with CPG.`,
    tags: ["overview", "basics", "platform"],
    tips: [],
  },
  {
    id: "q002",
    category: "introduction",
    question: "How does iLamp work?",
    answer: `iLamp operates as a closed-loop, grid-independent system:\n\n1. ENERGY GENERATION — Integrated solar panels harvest daylight and charge an onboard battery management system. The battery provides continuous power through day and night cycles.\n\n2. LIGHTING — A high-efficiency LED array provides street or area lighting, automatically adjusting brightness based on ambient conditions and motion detection. Dimming and scheduling reduce energy consumption during low-activity periods.\n\n3. EDGE AI COMPUTE — The NVIDIA Jetson Orin module runs AI inference tasks locally on the unit. This means data is processed at the point of capture — not sent to a distant cloud server — enabling real-time applications such as computer vision, traffic analytics, environmental sensing, and security monitoring.\n\n4. CONNECTIVITY — Units communicate via cellular, Wi-Fi, or mesh networking, enabling remote monitoring, firmware updates, and data transmission to cloud platforms or local servers.\n\n5. MANAGEMENT — The entire network of deployed units can be managed centrally through a cloud dashboard, providing status, health, energy output, and compute utilisation data in real time.`,
    tags: ["how it works", "solar", "AI", "operation"],
    tips: [],
  },
  {
    id: "q003",
    category: "introduction",
    question: "What are the main components of an iLamp unit?",
    answer: `Each iLamp unit consists of the following primary components:\n\n• SOLAR PANEL — Monocrystalline high-efficiency panel, sized to the deployment environment and latitude.\n\n• BATTERY SYSTEM — LiFePO4 (lithium iron phosphate) battery pack with integrated Battery Management System (BMS) for safety, health monitoring, and charge regulation.\n\n• LED LIGHT ENGINE — High-lumen, long-life LED luminaire with smart dimming capability.\n\n• NVIDIA JETSON ORIN MODULE — The AI compute brain of the unit. The Jetson AGX Orin delivers up to 275 TOPS (Tera Operations Per Second), enabling real-time inference of computer vision and AI models locally.\n\n• SENSORS — Environmental sensors (temperature, humidity, air quality), motion sensors, and optional cameras for AI vision tasks.\n\n• COMMUNICATIONS MODULE — Cellular (4G/5G), Wi-Fi, and/or LoRaWAN connectivity for remote management and data transmission.\n\n• POLE AND HOUSING — Engineered pole structure with weatherproof enclosures protecting all electronic components.\n\n• POWER MANAGEMENT CONTROLLER — Regulates energy flow between solar input, battery storage, lighting output, and compute module.`,
    tags: ["components", "hardware", "Jetson", "solar"],
    tips: [],
  },
  {
    id: "q004",
    category: "introduction",
    question: "Where do the components come from?",
    answer: `iLamp components are sourced through CPG's established supply chain, drawing on globally recognised manufacturers:\n\n• NVIDIA Jetson Orin modules are manufactured by NVIDIA (USA), the world leader in AI compute hardware.\n\n• Solar panels are sourced from Tier 1 solar manufacturers, selected for performance, warranty, and quality certification.\n\n• Battery systems use LiFePO4 chemistry from established battery manufacturers with full IEC certification.\n\n• LED luminaires are sourced from industrial lighting manufacturers meeting IK and IP ratings for outdoor deployment.\n\n• Poles and structural hardware can often be sourced or fabricated locally within the licence territory, which creates an important opportunity for local content compliance in PPP frameworks.\n\nCPG manages the global supply chain and component specifications. Licence holders do not procure components independently — orders are placed through CPG's commercial process, ensuring all units conform to the certified iLamp specification.`,
    tags: ["supply chain", "NVIDIA", "manufacturing", "components"],
    tips: [],
  },
  {
    id: "q005",
    category: "introduction",
    question: "Where can I find documents, specs, and user manuals?",
    answer: `All technical documentation, specifications, and user manuals are provided by CPG to licence holders through the CPG document portal and upon request from the CPG team.\n\nDocumentation available to licence holders includes:\n\n• iLamp Technical Specification Sheet\n• NVIDIA Jetson AGX Orin product datasheet\n• Installation and commissioning guide\n• Battery and solar sizing guide\n• Remote management platform user manual\n• Safety and compliance documentation\n• Territory white papers and deployment guides\n\nTo access documentation: contact CPG directly at the Conflow Power Group offices, or through your designated CPG territory representative.\n\nThis training platform will also be updated with direct document links as the network develops. Community members are encouraged to share field notes and installation tips in the Community Tips section.`,
    tags: ["documents", "manuals", "specs", "CPG"],
    tips: [],
  },
  // TECHNOLOGY
  {
    id: "q006",
    category: "technology",
    question: "What is AI Factories — and how does it work?",
    answer: `AI Factories is the distributed edge AI infrastructure layer built on top of the iLamp network. The concept transforms every deployed iLamp unit from a lighting asset into a node in a global AI compute network.\n\nEach NVIDIA Jetson Orin module embedded in an iLamp can run AI inference workloads — computer vision models, environmental monitoring, smart city analytics, security, traffic management, and more. When thousands of iLamp units are deployed across a territory, the aggregate compute capacity becomes significant.\n\nAI Factories monetises this compute in several ways:\n\n• LOCAL AI SERVICES — Authorities, businesses, and developers pay for AI inference services run on the network (e.g. number plate recognition, crowd monitoring, environmental data).\n\n• COMPUTE-AS-A-SERVICE — The edge compute capacity can be offered as a service layer to third-party developers and AI application providers.\n\n• COMMUNITY PROGRAM — The AI Factories Community Program provides a framework for territories to deploy iLamp as shared AI infrastructure serving government, enterprise, and citizens.\n\nBecause the compute is solar-powered and distributed, it is inherently green, resilient, and does not require expensive data centre infrastructure. This makes the tokens-per-watt efficiency of AI Factories a compelling benchmark against centralised cloud AI.`,
    tags: ["AI Factories", "edge AI", "compute", "NVIDIA"],
    tips: [],
  },
  {
    id: "q007",
    category: "technology",
    question: "What can be manufactured locally within a territory?",
    answer: `Local content is a significant advantage of the iLamp deployment model and is particularly important in PPP frameworks where governments require local economic participation.\n\nComponents that can typically be manufactured or sourced locally include:\n\n• POLE STRUCTURES — Steel or aluminium poles can be fabricated by local manufacturers to CPG specifications. This is often the largest single item by volume and weight.\n\n• CONCRETE FOUNDATIONS AND CIVILS — All ground preparation, foundation pouring, and civil installation work is performed by local contractors.\n\n• WIRING AND CONDUIT — Electrical wiring, conduit, junction boxes, and associated electrical materials can be locally sourced.\n\n• HOUSING ENCLOSURES — In some territories, weatherproof equipment enclosures can be fabricated locally.\n\n• INSTALLATION LABOUR — All installation, commissioning, and ongoing maintenance labour is local workforce.\n\n• ASSEMBLY — In higher-volume deployments, final assembly of non-proprietary elements can potentially be structured as a local assembly operation.\n\nThe higher-technology components — Jetson modules, solar panels, BMS, and LED engines — are sourced through CPG's supply chain. Local content strategies should be discussed with CPG when structuring PPP proposals, as local manufacturing commitments can significantly strengthen a bid.`,
    tags: ["local content", "manufacturing", "PPP", "installation"],
    tips: [],
  },
  {
    id: "q008",
    category: "technology",
    question: "How do I go about local manufacturing or assembly?",
    answer: `Establishing local manufacturing or assembly capability within a territory follows a structured process:\n\n1. ASSESS LOCAL CAPACITY — Identify what manufacturing capability exists in the territory (steel fabrication, electrical assembly, etc.).\n\n2. DISCUSS WITH CPG — Raise the local content objective with your CPG territory representative. CPG can advise on which components are eligible for local production without compromising unit certification.\n\n3. OBTAIN SPECIFICATIONS — CPG provides technical drawings and specifications for pole structures and enclosures that can be shared with local fabricators under confidentiality agreement.\n\n4. QUALIFY LOCAL SUPPLIERS — CPG will advise on quality standards and acceptance testing procedures. Local manufacturers must be approved before supplying into a deployment.\n\n5. STRUCTURE THE COMMERCIAL ARRANGEMENT — Local manufacturing is typically structured as a supply agreement between the local entity and the deployment vehicle, with pricing agreed in advance.\n\n6. INCORPORATE INTO PPP PROPOSAL — Local content commitments are documented in the PPP proposal as a commitment to host government, often expressed as a percentage of project value.\n\nThis is an evolving area of the iLamp commercial model. Licence holders with experience in local manufacturing are encouraged to share their approaches via the Community Tips section.`,
    tags: ["manufacturing", "local content", "assembly", "supply chain"],
    tips: [],
  },
  // REVENUE
  {
    id: "q009",
    category: "revenue",
    question: "How does the revenue work?",
    answer: `The iLamp platform generates revenue through multiple concurrent streams from the same physical infrastructure:\n\n1. LIGHTING SERVICE FEES — Municipalities, highway authorities, and private site operators pay a monthly service fee per unit for street lighting as a service (LaaS). This replaces capital expenditure with a predictable operational payment.\n\n2. AI COMPUTE / DATA SERVICES — Revenue from AI inference services run on the Jetson modules — smart city analytics, traffic monitoring, security, environmental sensing — charged to government or enterprise clients.\n\n3. CONNECTIVITY / ADVERTISING — Where permitted, iLamp units can carry small digital screens or connectivity hotspots generating advertising or connectivity revenue.\n\n4. CARBON CREDITS — Solar-powered infrastructure replacing grid-connected lighting generates measurable carbon offset credits, which can be sold on voluntary or compliance carbon markets.\n\n5. TERRITORY LICENSING FEES — Licence holders generate income by sublicensing to operators or contractors within their territory.\n\n6. PPP CONTRACT PAYMENTS — Under Public Private Partnership structures, government counterparties make availability or performance-based payments over multi-year contract terms.\n\nRevenue streams are designed to be stackable — a single deployed unit can generate income from several of these sources simultaneously, improving project economics significantly.`,
    tags: ["revenue", "business model", "LaaS", "carbon credits"],
    tips: [],
  },
  {
    id: "q010",
    category: "revenue",
    question: "What do I get of that revenue as a licence holder?",
    answer: `As an iLamp territory licence holder, your revenue entitlement is defined in your territorial licence agreement with CPG. The structure typically includes:\n\n• LICENCE REVENUE SHARE — A percentage of revenue generated from deployments within your licensed territory. The specific percentage is set in your agreement and varies by territory and deployment type.\n\n• SUBLICENSING INCOME — Where you sublicence deployment rights to operators, contractors, or municipal partners within your territory, the sublicence fee structure is set by you within the framework of your CPG agreement.\n\n• PROJECT DEVELOPMENT FEE — For projects you develop and bring to deployment, a development margin applies on the project value.\n\n• MANAGEMENT FEE — Ongoing asset management of a deployed iLamp network can carry a management service fee payable to the territory licence holder.\n\nFor specific revenue entitlement figures, refer to your territorial licence agreement or request a revenue model from CPG for your territory. CPG can prepare territory-specific financial projections based on deployment scale and applicable revenue streams.\n\nNote: Revenue share terms are commercially sensitive and specific to each territory agreement. This training platform provides the framework — your CPG agreement document is the authoritative source for your specific entitlements.`,
    tags: ["revenue share", "licence holder", "income", "commercial"],
    tips: [],
  },
  {
    id: "q011",
    category: "revenue",
    question: "How can I justify the revenue to a client or government partner?",
    answer: `Revenue justification is one of the most important skills for an iLamp licence holder. The following frameworks are effective:\n\n1. ENERGY COST REPLACEMENT — Traditional grid-connected streetlights cost municipalities £200–500+ per unit per year in electricity. iLamp eliminates this cost. Over a 20-year concession, this saving is substantial and forms the basis of payment capacity.\n\n2. CAPEX TO OPEX CONVERSION — Governments facing capital budget constraints can deploy iLamp under a service model, paying from operational budgets rather than capital programmes. The service fee is lower than the equivalent capital cost amortised over the same period.\n\n3. AI REVENUE OFFSET — AI compute revenue generated from the network can offset or subsidise the lighting service fee, reducing the net cost to government to near zero in high-utilisation scenarios.\n\n4. CARBON CREDIT VALUE — Carbon credits generated by solar-powered deployment have a monetisable value that can be assigned to reduce client cost or enhance project returns.\n\n5. TOKENS PER JOB / ECONOMIC MULTIPLIER — Where iLamp is deployed under a PPP framework, economic impact can be quantified: local jobs created, local procurement spend, skills development, and long-term tax revenue from enabled economic activity.\n\n6. COMPARATIVE BENCHMARKING — Compare total cost of ownership of iLamp over 10–20 years against conventional streetlight replacement, including maintenance, grid energy, and carbon costs. iLamp consistently demonstrates superior whole-life economics.\n\nCPG can assist with financial modelling for specific territory proposals upon request.`,
    tags: ["justification", "ROI", "business case", "client"],
    tips: [],
  },
  // TERRITORY
  {
    id: "q012",
    category: "territory",
    question: "How do I get a proposal built, and what information do I need to submit?",
    answer: `CPG will prepare a territory-specific commercial proposal upon request. To enable CPG to build a high-quality, accurate proposal, you should provide as much of the following information as possible:\n\n TERRITORY INFORMATION\n• Country and region/state/province\n• Population and major urban centres\n• Total number of existing streetlights (or estimated)\n• Current street lighting authority (municipal, national, private)\n• Key infrastructure corridors (highways, industrial zones, ports)\n\n DEPLOYMENT CONTEXT\n• Is there an immediate deployment opportunity (tender, government interest, site-specific project)?\n• Is this a new territory or a follow-on to existing discussions?\n• Preferred deployment model: PPP, direct sale, service contract, or licence sublicence?\n\n COMMERCIAL PARAMETERS\n• Indicative unit volumes for Phase 1 and long-term\n• Available financing or investment sources (government budget, DFI, private investment)\n• Local partner or counterparty if identified\n\n REGULATORY CONTEXT\n• Are there local content requirements?\n• Any relevant government programmes (smart city, green energy, rural electrification)?\n\n YOUR CONTACT DETAILS\n• Full name, organisation, role\n• Territory licence agreement reference (if existing licence holder)\n\nSubmit this information to CPG, or via your territory representative. CPG typically prepares proposals within 5–10 business days of receiving sufficient information.`,
    tags: ["proposal", "territory", "submission", "CPG"],
    tips: [],
  },
  {
    id: "q013",
    category: "territory",
    question: "How can I buy a territory licence?",
    answer: `Territorial licences are available for most countries and regions globally. The acquisition process follows these steps:\n\n1. IDENTIFY YOUR TERRITORY — Define the geographic scope: a country, region, state, or city. Some territories are exclusive, others are non-exclusive depending on market size and prior commitments.\n\n2. INITIAL ENQUIRY — Contact CPG to confirm availability and receive an outline of the licence terms applicable to your territory of interest.\n\n3. REVIEW TERM SHEET — CPG will provide a territory licence term sheet covering: licence scope, exclusivity, term (typically 10–25 years), deposit, ongoing obligations, and revenue framework.\n\n4. DUE DILIGENCE — Both parties undertake appropriate due diligence. CPG reviews the licence applicant's capability and capacity; the applicant reviews the iLamp platform and commercial model.\n\n5. EXECUTE AGREEMENT — The territorial licence agreement is executed between the applicant entity and CPG. Legal review by both parties is standard.\n\n6. PAY DEPOSIT — The licence deposit (see separate FAQ) is paid upon or shortly after execution.\n\n7. ONBOARDING — CPG provides territory onboarding including access to documentation, training (including this platform), and introduction to the CPG support network.\n\nTerritory licences are a significant commercial asset. They convey the right to deploy, sublicense, and commercialise the iLamp platform within the defined territory under the CPG brand and technology framework.`,
    tags: ["territory", "licence", "acquisition", "agreement"],
    tips: [],
  },
  {
    id: "q014",
    category: "territory",
    question: "What is the deposit for a territory?",
    answer: `Territory licence deposits vary based on territory size, population, market potential, and exclusivity. CPG sets deposit levels on a territory-by-territory basis.\n\nGeneral principles:\n\n• Deposits reflect the strategic value and scale of the territory. A major country licence carries a higher deposit than a city or regional licence.\n\n• The deposit is a commitment payment demonstrating the licence holder's seriousness and ability to develop the territory. It is not typically refundable if the licence holder fails to develop the territory.\n\n• In some structures, the deposit may be applied against future CPG invoices (unit purchases, services) once deployment commences — this should be confirmed in your specific agreement.\n\n• Deposits for territories can range from modest amounts for smaller or emerging market territories to significant six-figure sums for major, exclusive national licences.\n\nFor the exact deposit applicable to a specific territory, contact CPG directly. CPG will confirm the deposit structure as part of the licence term sheet process.\n\nNote: This platform provides general guidance. Your CPG territory agreement is the definitive document for all financial commitments.`,
    tags: ["deposit", "territory", "licence fee", "payment"],
    tips: [],
  },
  // FINANCE
  {
    id: "q015",
    category: "finance",
    question: "How does the project financing work, and what am I liable for?",
    answer: `iLamp deployments can be financed through several mechanisms, and the liability profile depends on the structure chosen:\n\n PROJECT FINANCE STRUCTURES\n\n1. PPP / CONCESSION FINANCE — Government counterparty commits to availability payments over the concession term (typically 15–25 years). This payment stream is used as security for debt finance from banks or Development Finance Institutions (DFIs). The special purpose vehicle (SPV) holding the PPP contract is the primary borrower. Shareholder liability is typically limited to equity invested plus any parent guarantees negotiated by lenders.\n\n2. EQUIPMENT LEASING / VENDOR FINANCE — Units can be financed on a lease basis, where the lessee (operator or municipality) makes monthly payments over the equipment life. Liability is contained within the leasing entity.\n\n3. DIRECT INVESTMENT — Investors provide equity or mezzanine finance to fund unit procurement and deployment, taking a share of revenue returns. Investor liability is limited to capital invested.\n\n4. LOAN FINANCE — Corporate or project loans from commercial banks, secured against contracted revenue streams or project assets. The borrowing entity is liable for loan repayment; personal guarantees may be sought from directors of smaller entities.\n\n IMPORTANT\nLiability in any financing structure depends on the legal structure you establish, the terms negotiated with lenders, and the jurisdiction. Always obtain independent legal and financial advice before entering financing commitments. CPG can introduce specialist infrastructure finance advisers upon request.`,
    tags: ["finance", "loans", "liability", "PPP", "investment"],
    tips: [],
  },
  {
    id: "q016",
    category: "finance",
    question: "What is a PPP (Public Private Partnership)?",
    answer: `A Public Private Partnership (PPP) is a long-term contractual arrangement between a government entity and a private sector party to deliver public infrastructure or services.\n\nIn the context of iLamp, PPP structures are used to deploy street lighting and AI infrastructure where:\n\n• The government (national, state, or municipal) requires the infrastructure but lacks the capital budget to fund it upfront.\n• The private party (the iLamp licence holder or deployment entity) finances, builds, and operates the infrastructure.\n• The government pays for the service over time through availability payments, service fees, or revenue sharing.\n\n KEY PPP CHARACTERISTICS\n• LONG TERM: Typically 15–25 years.\n• RISK TRANSFER: Construction, operational, and performance risks are transferred to the private party.\n• OUTPUT-BASED: Government pays for performance (lights working, data delivered) not for assets.\n• BANKABLE: The payment stream from government is used to raise private finance.\n• LOCAL BENEFIT: PPPs typically include local content, employment, and skills development commitments.\n\n WHY PPP WORKS FOR iLAMP\niLamp is ideally suited to PPP because: it generates multiple revenue streams (not just a single service payment); solar independence removes energy cost risk; the AI compute layer adds value that government can monetise separately; and the long operational life supports long concession terms.\n\nCPG has developed PPP proposal frameworks for multiple territories and can assist licence holders in structuring and presenting PPP proposals.`,
    tags: ["PPP", "government", "public private partnership", "concession"],
    tips: [],
  },
  {
    id: "q017",
    category: "finance",
    question: "What is a Green Utility?",
    answer: `A Green Utility is a private or public entity that delivers utility-grade infrastructure services — lighting, power, connectivity, compute — using renewable energy sources, structured and operated like a traditional utility company but with a sustainability mandate.\n\nIn the iLamp context, a Green Utility is the deployment model for operating an iLamp network as an infrastructure utility:\n\n• SERVICE DELIVERY: The Green Utility provides street lighting as a service to municipalities, paying for and operating the infrastructure on behalf of the public authority.\n\n• REVENUE MODEL: Like a water or electricity utility, the Green Utility charges a regular service fee (per unit per month) for the services it delivers. This fee covers capital recovery, operations, maintenance, and return on investment.\n\n• RENEWABLE GENERATION: Because iLamp is solar-powered, the Green Utility generates its own energy — making it independent of grid tariffs and fossil fuel price volatility.\n\n• CARBON POSITIVE: A Green Utility operating iLamp generates carbon credits from its renewable generation, creating an additional revenue stream and ESG credentials.\n\n• AI SERVICES LAYER: The AI compute embedded in each unit allows the Green Utility to offer data services alongside lighting — effectively becoming a digital infrastructure provider.\n\nThe Green Utility model is particularly powerful in territories where:\n- Municipal budgets are constrained\n- Grid infrastructure is unreliable\n- Governments have renewable energy or smart city commitments\n- International development finance is available for green infrastructure\n\nEstablishing an iLamp Green Utility in your territory is one of the most strategic structures available to licence holders.`,
    tags: ["green utility", "utility model", "renewable", "sustainability"],
    tips: [],
  },
];

// ── Main App ───────────────────────────────────────────────────────────────
export default function ILampTrainingDashboard() {
  const [qaData, setQaData] = useState(DEFAULT_QA);
  const [categories] = useState(DEFAULT_CATEGORIES);
  const [activeCategory, setActiveCategory] = useState("all");
  const [searchQuery, setSearchQuery] = useState("");
  const [expandedId, setExpandedId] = useState(null);
  const [adminMode, setAdminMode] = useState(false);
  const [adminPin, setAdminPin] = useState("");
  const [showAdminLogin, setShowAdminLogin] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [showAddForm, setShowAddForm] = useState(false);
  const [newQA, setNewQA] = useState({
    category: "introduction",
    question: "",
    answer: "",
    tags: "",
  });
  const [tipForm, setTipForm] = useState({ id: null, author: "", text: "" });
  const [showTipForm, setShowTipForm] = useState(null);
  const [loaded, setLoaded] = useState(false);
  const [notification, setNotification] = useState(null);
  const searchRef = useRef(null);

  const PIN = "2611";

  // ── Storage - Using localStorage instead of window.storage ──
  useEffect(() => {
    async function load() {
      try {
        const storedData = localStorage.getItem("ilamp-kb-v2");
        if (storedData) {
          setQaData(JSON.parse(storedData));
        }
      } catch (err) {
        console.error("Error loading data:", err);
      }
      setLoaded(true);
    }
    load();
  }, []);

  const save = useCallback((data) => {
    try {
      localStorage.setItem("ilamp-kb-v2", JSON.stringify(data));
    } catch (err) {
      console.error("Error saving data:", err);
    }
  }, []);

  const notify = (msg, type = "success") => {
    setNotification({ msg, type });
    setTimeout(() => setNotification(null), 3000);
  };

  // ── Filtering ──
  const filtered = qaData.filter((item) => {
    const matchCat =
      activeCategory === "all" || item.category === activeCategory;
    if (!searchQuery) return matchCat;
    const q = searchQuery.toLowerCase();
    return (
      matchCat &&
      (item.question.toLowerCase().includes(q) ||
        item.answer.toLowerCase().includes(q) ||
        item.tags.some((t) => t.includes(q)))
    );
  });

  // ── Admin ──
  const handleAdminLogin = () => {
    if (adminPin === PIN) {
      setAdminMode(true);
      setShowAdminLogin(false);
      setAdminPin("");
      notify("Admin mode activated");
    } else {
      notify("Incorrect PIN", "error");
    }
  };

  // ── Edit ──
  const startEdit = (item) => {
    setEditingId(item.id);
    setEditForm({
      question: item.question,
      answer: item.answer,
      tags: item.tags.join(", "),
    });
  };

  const saveEdit = () => {
    const updated = qaData.map((item) =>
      item.id === editingId
        ? {
            ...item,
            question: editForm.question,
            answer: editForm.answer,
            tags: editForm.tags.split(",").map((t) => t.trim()).filter(Boolean),
          }
        : item
    );
    setQaData(updated);
    save(updated);
    setEditingId(null);
    notify("Entry updated");
  };

  const deleteEntry = (id) => {
    if (!window.confirm("Delete this entry?")) return;
    const updated = qaData.filter((item) => item.id !== id);
    setQaData(updated);
    save(updated);
    notify("Entry deleted");
  };

  // ── Add ──
  const addEntry = () => {
    if (!newQA.question.trim() || !newQA.answer.trim()) {
      notify("Question and answer required", "error");
      return;
    }
    const entry = {
      id: "q" + Date.now(),
      category: newQA.category,
      question: newQA.question,
      answer: newQA.answer,
      tags: newQA.tags.split(",").map((t) => t.trim()).filter(Boolean),
      tips: [],
    };
    const updated = [...qaData, entry];
    setQaData(updated);
    save(updated);
    setNewQA({ category: "introduction", question: "", answer: "", tags: "" });
    setShowAddForm(false);
    notify("Entry added");
  };

  // ── Community Tips ──
  const submitTip = (itemId) => {
    if (!tipForm.text.trim()) return;
    const updated = qaData.map((item) =>
      item.id === itemId
        ? {
            ...item,
            tips: [
              ...item.tips,
              {
                id: Date.now(),
                author: tipForm.author || "Anonymous",
                text: tipForm.text,
                date: new Date().toLocaleDateString("en-GB"),
              },
            ],
          }
        : item
    );
    setQaData(updated);
    save(updated);
    setTipForm({ id: null, author: "", text: "" });
    setShowTipForm(null);
    notify("Tip added — thank you!");
  };

  const deleteTip = (itemId, tipId) => {
    const updated = qaData.map((item) =>
      item.id === itemId
        ? { ...item, tips: item.tips.filter((t) => t.id !== tipId) }
        : item
    );
    setQaData(updated);
    save(updated);
  };

  const getCategoryColor = (id) =>
    categories.find((c) => c.id === id)?.color || "#888";

  if (!loaded) {
    return (
      <div style={styles.loadingScreen}>
        <div style={styles.loadingText}>LOADING iLAMP KNOWLEDGE BASE</div>
        <div style={styles.loadingBar}>
          <div style={styles.loadingFill} />
        </div>
      </div>
    );
  }

  return (
    <div style={styles.root}>
      {/* Notification */}
      {notification && (
        <div
          style={{
            ...styles.notification,
            background: notification.type === "error" ? "#c0392b" : "#1a6b3a",
          }}
        >
          {notification.msg}
        </div>
      )}

      {/* Header */}
      <header style={styles.header}>
        <div style={styles.headerLeft}>
          <div style={styles.logoMark}>⬡</div>
          <div>
            <div style={styles.headerTitle}>iLAMP KNOWLEDGE BASE</div>
            <div style={styles.headerSub}>
              LICENCE HOLDER TRAINING PLATFORM · CONFLOW POWER GROUP
            </div>
          </div>
        </div>
        <div style={styles.headerRight}>
          <div style={styles.statBadge}>
            <span style={styles.statNum}>{qaData.length}</span>
            <span style={styles.statLabel}>ENTRIES</span>
          </div>
          <div style={styles.statBadge}>
            <span style={styles.statNum}>
              {qaData.reduce((a, b) => a + b.tips.length, 0)}
            </span>
            <span style={styles.statLabel}>TIPS</span>
          </div>
          {adminMode ? (
            <button
              style={styles.adminBtn}
              onClick={() => {
                setAdminMode(false);
                notify("Admin mode off");
              }}
            >
              ◉ ADMIN ON
            </button>
          ) : (
            <button
              style={{ ...styles.adminBtn, background: "#1a1a2e", color: "#666" }}
              onClick={() => setShowAdminLogin(true)}
            >
              ◎ ADMIN
            </button>
          )}
        </div>
      </header>

      {/* Admin Login Modal */}
      {showAdminLogin && (
        <div style={styles.modal}>
          <div style={styles.modalBox}>
            <div style={styles.modalTitle}>ADMIN ACCESS</div>
            <input
              type="password"
              placeholder="Enter PIN"
              value={adminPin}
              onChange={(e) => setAdminPin(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && handleAdminLogin()}
              style={styles.modalInput}
              autoFocus
            />
            <div style={styles.modalButtons}>
              <button style={styles.btnPrimary} onClick={handleAdminLogin}>
                UNLOCK
              </button>
              <button
                style={styles.btnSecondary}
                onClick={() => {
                  setShowAdminLogin(false);
                  setAdminPin("");
                }}
              >
                CANCEL
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Search Bar */}
      <div style={styles.searchRow}>
        <div style={styles.searchWrap}>
          <span style={styles.searchIcon}>⌕</span>
          <input
            ref={searchRef}
            style={styles.searchInput}
            placeholder="Search questions, answers, tags..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
          {searchQuery && (
            <button
              style={styles.clearSearch}
              onClick={() => setSearchQuery("")}
            >
              ✕
            </button>
          )}
        </div>
        {adminMode && (
          <button
            style={styles.addBtn}
            onClick={() => setShowAddForm(!showAddForm)}
          >
            + ADD ENTRY
          </button>
        )}
      </div>

      {/* Add Form */}
      {adminMode && showAddForm && (
        <div style={styles.addForm}>
          <div style={styles.addFormTitle}>NEW KNOWLEDGE ENTRY</div>
          <select
            style={styles.formSelect}
            value={newQA.category}
            onChange={(e) => setNewQA({ ...newQA, category: e.target.value })}
          >
            {categories.map((c) => (
              <option key={c.id} value={c.id}>
                {c.label}
              </option>
            ))}
          </select>
          <input
            style={styles.formInput}
            placeholder="Question"
            value={newQA.question}
            onChange={(e) => setNewQA({ ...newQA, question: e.target.value })}
          />
          <textarea
            style={styles.formTextarea}
            placeholder="Answer (use \n for new lines)"
            value={newQA.answer}
            onChange={(e) => setNewQA({ ...newQA, answer: e.target.value })}
            rows={8}
          />
          <input
            style={styles.formInput}
            placeholder="Tags (comma separated)"
            value={newQA.tags}
            onChange={(e) => setNewQA({ ...newQA, tags: e.target.value })}
          />
          <div style={styles.modalButtons}>
            <button style={styles.btnPrimary} onClick={addEntry}>
              SAVE ENTRY
            </button>
            <button
              style={styles.btnSecondary}
              onClick={() => setShowAddForm(false)}
            >
              CANCEL
            </button>
          </div>
        </div>
      )}

      {/* Category Nav */}
      <nav style={styles.catNav}>
        <button
          style={{
            ...styles.catBtn,
            ...(activeCategory === "all" ? styles.catBtnActive : {}),
          }}
          onClick={() => setActiveCategory("all")}
        >
          <span>◈</span> ALL
          <span style={styles.catCount}>{qaData.length}</span>
        </button>
        {categories.map((cat) => {
          const count = qaData.filter((q) => q.category === cat.id).length;
          return (
            <button
              key={cat.id}
              style={{
                ...styles.catBtn,
                ...(activeCategory === cat.id
                  ? { ...styles.catBtnActive, borderColor: cat.color, color: cat.color }
                  : {}),
              }}
              onClick={() => setActiveCategory(cat.id)}
            >
              <span>{cat.icon}</span> {cat.label.toUpperCase()}
              <span style={styles.catCount}>{count}</span>
            </button>
          );
        })}
      </nav>

      {/* Results Count */}
      <div style={styles.resultsBar}>
        {searchQuery && (
          <span>
            {filtered.length} result{filtered.length !== 1 ? "s" : ""} for "
            {searchQuery}"
          </span>
        )}
      </div>

      {/* Q&A List */}
      <main style={styles.main}>
        {filtered.length === 0 ? (
          <div style={styles.empty}>
            <div style={styles.emptyIcon}>◎</div>
            <div>No entries found</div>
            {adminMode && (
              <div style={{ fontSize: 13, color: "#777", marginTop: 8 }}>
                Use + ADD ENTRY to create the first entry in this category
              </div>
            )}
          </div>
        ) : (
          filtered.map((item) => {
            const isExpanded = expandedId === item.id;
            const isEditing = editingId === item.id;
            const catColor = getCategoryColor(item.category);
            const catObj = categories.find((c) => c.id === item.category);

            return (
              <div key={item.id} style={styles.card}>
                {/* Card Header */}
                <div
                  style={{ ...styles.cardHeader, cursor: "pointer" }}
                  onClick={() =>
                    setExpandedId(isExpanded ? null : item.id)
                  }
                >
                  <div style={styles.cardHeaderLeft}>
                    <div
                      style={{
                        ...styles.catPill,
                        color: catColor,
                        borderColor: catColor + "44",
                        background: catColor + "11",
                      }}
                    >
                      {catObj?.icon} {catObj?.label.toUpperCase()}
                    </div>
                    <div style={styles.question}>{item.question}</div>
                  </div>
                  <div style={styles.cardHeaderRight}>
                    {item.tips.length > 0 && (
                      <span style={styles.tipsBadge}>
                        ✦ {item.tips.length}
                      </span>
                    )}
                    <div
                      style={{
                        ...styles.chevron,
                        transform: isExpanded
                          ? "rotate(180deg)"
                          : "rotate(0deg)",
                      }}
                    >
                      ⌄
                    </div>
                  </div>
                </div>

                {/* Expanded Content */}
                {isExpanded && (
                  <div style={styles.cardBody}>
                    {isEditing ? (
                      // Edit Mode
                      <div style={styles.editForm}>
                        <input
                          style={styles.formInput}
                          value={editForm.question}
                          onChange={(e) =>
                            setEditForm({ ...editForm, question: e.target.value })
                          }
                        />
                        <textarea
                          style={styles.formTextarea}
                          value={editForm.answer}
                          onChange={(e) =>
                            setEditForm({ ...editForm, answer: e.target.value })
                          }
                          rows={12}
                        />
                        <input
                          style={styles.formInput}
                          value={editForm.tags}
                          onChange={(e) =>
                            setEditForm({ ...editForm, tags: e.target.value })
                          }
                          placeholder="Tags (comma separated)"
                        />
                        <div style={styles.modalButtons}>
                          <button style={styles.btnPrimary} onClick={saveEdit}>
                            SAVE
                          </button>
                          <button
                            style={styles.btnSecondary}
                            onClick={() => setEditingId(null)}
                          >
                            CANCEL
                          </button>
                        </div>
                      </div>
                    ) : (
                      // View Mode
                      <>
                        <div style={styles.answer}>
                          {item.answer.split("\n").map((line, i) => (
                            <p key={i} style={styles.answerLine}>
                              {line}
                            </p>
                          ))}
                        </div>

                        {/* Tags */}
                        {item.tags.length > 0 && (
                          <div style={styles.tags}>
                            {item.tags.map((tag) => (
                              <span
                                key={tag}
                                style={styles.tag}
                                onClick={() => setSearchQuery(tag)}
                              >
                                #{tag}
                              </span>
                            ))}
                          </div>
                        )}

                        {/* Admin Actions */}
                        {adminMode && (
                          <div style={styles.adminActions}>
                            <button
                              style={styles.editBtn}
                              onClick={() => startEdit(item)}
                            >
                              ✎ EDIT
                            </button>
                            <button
                              style={styles.deleteBtn}
                              onClick={() => deleteEntry(item.id)}
                            >
                              ✕ DELETE
                            </button>
                          </div>
                        )}

                        {/* Community Tips */}
                        {item.tips.length > 0 && (
                          <div style={styles.tipsSection}>
                            <div style={styles.tipsSectionTitle}>
                              ✦ FIELD TIPS & DISCOVERIES
                            </div>
                            {item.tips.map((tip) => (
                              <div key={tip.id} style={styles.tipCard}>
                                <div style={styles.tipHeader}>
                                  <span style={styles.tipAuthor}>
                                    {tip.author}
                                  </span>
                                  <span style={styles.tipDate}>{tip.date}</span>
                                  {adminMode && (
                                    <button
                                      style={styles.tipDelete}
                                      onClick={() =>
                                        deleteTip(item.id, tip.id)
                                      }
                                    >
                                      ✕
                                    </button>
                                  )}
                                </div>
                                <div style={styles.tipText}>{tip.text}</div>
                              </div>
                            ))}
                          </div>
                        )}

                        {/* Add Tip */}
                        {showTipForm === item.id ? (
                          <div style={styles.tipFormWrap}>
                            <div style={styles.tipFormTitle}>
                              ADD A FIELD TIP OR DISCOVERY
                            </div>
                            <input
                              style={styles.formInput}
                              placeholder="Your name / organisation (optional)"
                              value={tipForm.author}
                              onChange={(e) =>
                                setTipForm({ ...tipForm, author: e.target.value })
                              }
                            />
                            <textarea
                              style={styles.formTextarea}
                              placeholder="Share your experience, tip, or discovery..."
                              value={tipForm.text}
                              onChange={(e) =>
                                setTipForm({ ...tipForm, text: e.target.value })
                              }
                              rows={4}
                            />
                            <div style={styles.modalButtons}>
                              <button
                                style={styles.btnPrimary}
                                onClick={() => submitTip(item.id)}
                              >
                                SUBMIT TIP
                              </button>
                              <button
                                style={styles.btnSecondary}
                                onClick={() => setShowTipForm(null)}
                              >
                                CANCEL
                              </button>
                            </div>
                          </div>
                        ) : (
                          <button
                            style={styles.addTipBtn}
                            onClick={() => setShowTipForm(item.id)}
                          >
                            ✦ ADD FIELD TIP
                          </button>
                        )}
                      </>
                    )}
                  </div>
                )}
              </div>
            );
          })
        )}
      </main>

      {/* Footer */}
      <footer style={styles.footer}>
        <span>iLAMP KNOWLEDGE BASE · CONFLOW POWER GROUP LIMITED</span>
        <span>v1.0 · {new Date().getFullYear()}</span>
      </footer>
    </div>
  );
}

// ── Styles ─────────────────────────────────────────────────────────────────
const styles = {
  root: {
    minHeight: "100vh",
    background: "#0a0a0f",
    color: "#d4d4d8",
    fontFamily: "'Courier New', 'Lucida Console', monospace",
    fontSize: 14,
  },
  loadingScreen: {
    height: "100vh",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    background: "#0a0a0f",
    gap: 24,
  },
  loadingText: {
    color: "#0071e3",
    fontFamily: "'Courier New', monospace",
    letterSpacing: 6,
    fontSize: 13,
  },
  loadingBar: {
    width: 240,
    height: 2,
    background: "#1a1a2e",
    overflow: "hidden",
  },
  loadingFill: {
    height: "100%",
    width: "60%",
    background: "#0071e3",
    animation: "none",
  },
  notification: {
    position: "fixed",
    top: 16,
    right: 16,
    zIndex: 9999,
    padding: "10px 20px",
    color: "#fff",
    fontFamily: "'Courier New', monospace",
    fontSize: 12,
    letterSpacing: 1,
    borderRadius: 2,
    boxShadow: "0 4px 20px rgba(0,0,0,0.5)",
  },
  header: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    padding: "20px 28px",
    borderBottom: "1px solid #1e1e2e",
    background: "#080810",
    flexWrap: "wrap",
    gap: 12,
  },
  headerLeft: {
    display: "flex",
    alignItems: "center",
    gap: 16,
  },
  logoMark: {
    fontSize: 32,
    color: "#0071e3",
    lineHeight: 1,
  },
  headerTitle: {
    fontSize: 18,
    fontWeight: "bold",
    color: "#0071e3",
    letterSpacing: 4,
  },
  headerSub: {
    fontSize: 10,
    color: "#888",
    letterSpacing: 2,
    marginTop: 3,
  },
  headerRight: {
    display: "flex",
    alignItems: "center",
    gap: 12,
  },
  statBadge: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    padding: "6px 14px",
    background: "#0f0f1a",
    border: "1px solid #1e1e2e",
    gap: 2,
  },
  statNum: {
    fontSize: 18,
    color: "#0071e3",
    fontWeight: "bold",
  },
  statLabel: {
    fontSize: 9,
    color: "#666",
    letterSpacing: 2,
  },
  adminBtn: {
    background: "#1a2e1a",
    color: "#4ade80",
    border: "1px solid #2a4a2a",
    padding: "8px 14px",
    fontSize: 10,
    letterSpacing: 2,
    cursor: "pointer",
    fontFamily: "'Courier New', monospace",
  },
  searchRow: {
    display: "flex",
    gap: 12,
    padding: "16px 28px",
    borderBottom: "1px solid #1a1a2a",
    background: "#0c0c18",
  },
  searchWrap: {
    flex: 1,
    position: "relative",
    display: "flex",
    alignItems: "center",
  },
  searchIcon: {
    position: "absolute",
    left: 12,
    fontSize: 18,
    color: "#666",
    pointerEvents: "none",
  },
  searchInput: {
    width: "100%",
    background: "#0a0a14",
    border: "1px solid #2a2a3a",
    color: "#d4d4d8",
    padding: "10px 40px 10px 40px",
    fontFamily: "'Courier New', monospace",
    fontSize: 13,
    outline: "none",
    boxSizing: "border-box",
  },
  clearSearch: {
    position: "absolute",
    right: 12,
    background: "none",
    border: "none",
    color: "#777",
    cursor: "pointer",
    fontSize: 12,
  },
  addBtn: {
    background: "#1a3a1a",
    border: "1px solid #2a5a2a",
    color: "#4ade80",
    padding: "10px 18px",
    fontSize: 11,
    letterSpacing: 2,
    cursor: "pointer",
    fontFamily: "'Courier New', monospace",
    whiteSpace: "nowrap",
  },
  addForm: {
    background: "#0c0c18",
    border: "1px solid #2a2a3a",
    margin: "0 28px 16px",
    padding: 20,
    display: "flex",
    flexDirection: "column",
    gap: 10,
  },
  addFormTitle: {
    color: "#0071e3",
    letterSpacing: 3,
    fontSize: 11,
    marginBottom: 4,
  },
  catNav: {
    display: "flex",
    gap: 6,
    padding: "14px 28px",
    borderBottom: "1px solid #1a1a2a",
    overflowX: "auto",
    flexWrap: "wrap",
  },
  catBtn: {
    background: "#0a0a14",
    border: "1px solid #2a2a3a",
    color: "#666",
    padding: "7px 14px",
    fontSize: 10,
    letterSpacing: 1.5,
    cursor: "pointer",
    fontFamily: "'Courier New', monospace",
    display: "flex",
    alignItems: "center",
    gap: 6,
    whiteSpace: "nowrap",
    transition: "all 0.15s",
  },
  catBtnActive: {
    borderColor: "#0071e3",
    color: "#0071e3",
    background: "#001a33",
  },
  catCount: {
    background: "#1a1a2a",
    borderRadius: 2,
    padding: "1px 5px",
    fontSize: 9,
    color: "#666",
  },
  resultsBar: {
    padding: "8px 28px",
    fontSize: 11,
    color: "#888",
    letterSpacing: 1,
    minHeight: 26,
  },
  main: {
    padding: "0 28px 40px",
    maxWidth: 960,
    margin: "0 auto",
    display: "flex",
    flexDirection: "column",
    gap: 8,
  },
  card: {
    background: "#0c0c18",
    border: "1px solid #1e1e2e",
    overflow: "hidden",
    transition: "border-color 0.2s",
  },
  cardHeader: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "flex-start",
    padding: "16px 20px",
    gap: 12,
  },
  cardHeaderLeft: {
    display: "flex",
    flexDirection: "column",
    gap: 8,
    flex: 1,
  },
  cardHeaderRight: {
    display: "flex",
    alignItems: "center",
    gap: 10,
    flexShrink: 0,
  },
  catPill: {
    display: "inline-flex",
    alignItems: "center",
    gap: 4,
    fontSize: 9,
    letterSpacing: 2,
    padding: "3px 8px",
    border: "1px solid",
    width: "fit-content",
  },
  question: {
    fontSize: 15,
    color: "#e4e4e8",
    fontWeight: "bold",
    letterSpacing: 0.3,
    lineHeight: 1.4,
  },
  tipsBadge: {
    color: "#FFD93D",
    fontSize: 10,
    letterSpacing: 1,
  },
  chevron: {
    fontSize: 18,
    color: "#666",
    transition: "transform 0.2s",
    lineHeight: 1,
  },
  cardBody: {
    padding: "0 20px 20px",
    borderTop: "1px solid #1a1a2a",
    paddingTop: 16,
  },
  answer: {
    lineHeight: 1.75,
    color: "#b4b4b8",
    marginBottom: 16,
  },
  answerLine: {
    margin: "4px 0",
    fontSize: 13.5,
  },
  tags: {
    display: "flex",
    flexWrap: "wrap",
    gap: 6,
    marginBottom: 14,
  },
  tag: {
    fontSize: 10,
    color: "#777",
    border: "1px solid #2a2a3a",
    padding: "2px 8px",
    cursor: "pointer",
    letterSpacing: 0.5,
    transition: "color 0.15s",
  },
  adminActions: {
    display: "flex",
    gap: 8,
    marginBottom: 14,
    paddingTop: 8,
    borderTop: "1px solid #2a2a3a",
  },
  editBtn: {
    background: "#1a2a1a",
    border: "1px solid #2a4a2a",
    color: "#4ade80",
    padding: "5px 12px",
    fontSize: 10,
    cursor: "pointer",
    fontFamily: "'Courier New', monospace",
    letterSpacing: 1,
  },
  deleteBtn: {
    background: "#2a1a1a",
    border: "1px solid #4a2a2a",
    color: "#ff6b6b",
    padding: "5px 12px",
    fontSize: 10,
    cursor: "pointer",
    fontFamily: "'Courier New', monospace",
    letterSpacing: 1,
  },
  tipsSection: {
    marginTop: 20,
    paddingTop: 16,
    borderTop: "1px solid #2a2a3a",
  },
  tipsSectionTitle: {
    color: "#FFD93D",
    fontSize: 11,
    letterSpacing: 2,
    marginBottom: 12,
  },
  tipCard: {
    background: "#141420",
    border: "1px solid #2a2a3a",
    padding: 12,
    marginBottom: 8,
    fontSize: 12,
  },
  tipHeader: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: 8,
  },
  tipAuthor: {
    color: "#FFD93D",
    fontSize: 11,
    fontWeight: "bold",
  },
  tipDate: {
    color: "#777",
    fontSize: 10,
  },
  tipDelete: {
    background: "none",
    border: "none",
    color: "#777",
    cursor: "pointer",
    fontSize: 10,
    padding: "2px 6px",
  },
  tipText: {
    color: "#a4a4a8",
    lineHeight: 1.5,
  },
  tipFormWrap: {
    marginTop: 20,
    paddingTop: 16,
    borderTop: "1px solid #2a2a3a",
  },
  tipFormTitle: {
    color: "#FFD93D",
    fontSize: 11,
    letterSpacing: 2,
    marginBottom: 12,
  },
  addTipBtn: {
    background: "#1a1a00",
    border: "1px solid #3a3a00",
    color: "#FFD93D",
    padding: "8px 16px",
    fontSize: 10,
    letterSpacing: 1,
    cursor: "pointer",
    fontFamily: "'Courier New', monospace",
    marginTop: 12,
    display: "inline-block",
  },
  editForm: {
    display: "flex",
    flexDirection: "column",
    gap: 10,
  },
  formInput: {
    background: "#0a0a14",
    border: "1px solid #2a2a3a",
    color: "#d4d4d8",
    padding: 10,
    fontFamily: "'Courier New', monospace",
    fontSize: 12,
    outline: "none",
  },
  formTextarea: {
    background: "#0a0a14",
    border: "1px solid #2a2a3a",
    color: "#d4d4d8",
    padding: 10,
    fontFamily: "'Courier New', monospace",
    fontSize: 12,
    outline: "none",
    resize: "vertical",
  },
  formSelect: {
    background: "#0a0a14",
    border: "1px solid #2a2a3a",
    color: "#d4d4d8",
    padding: 10,
    fontFamily: "'Courier New', monospace",
    fontSize: 12,
    outline: "none",
  },
  modalButtons: {
    display: "flex",
    gap: 8,
    marginTop: 8,
  },
  btnPrimary: {
    background: "#0071e3",
    color: "#fff",
    border: "none",
    padding: "8px 16px",
    fontSize: 10,
    letterSpacing: 1,
    cursor: "pointer",
    fontFamily: "'Courier New', monospace",
    fontWeight: "bold",
  },
  btnSecondary: {
    background: "#1a1a2a",
    color: "#888",
    border: "1px solid #2a2a3a",
    padding: "8px 16px",
    fontSize: 10,
    letterSpacing: 1,
    cursor: "pointer",
    fontFamily: "'Courier New', monospace",
  },
  modal: {
    position: "fixed",
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: "rgba(0,0,0,0.85)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    zIndex: 10000,
  },
  modalBox: {
    background: "#0c0c18",
    border: "1px solid #2a2a3a",
    padding: 28,
    minWidth: 320,
    display: "flex",
    flexDirection: "column",
    gap: 16,
  },
  modalTitle: {
    color: "#0071e3",
    fontSize: 12,
    letterSpacing: 3,
  },
  modalInput: {
    background: "#0a0a14",
    border: "1px solid #2a2a3a",
    color: "#d4d4d8",
    padding: 10,
    fontFamily: "'Courier New', monospace",
    fontSize: 13,
    outline: "none",
    width: "100%",
    boxSizing: "border-box",
  },
  empty: {
    textAlign: "center",
    padding: 60,
    color: "#666",
  },
  emptyIcon: {
    fontSize: 48,
    marginBottom: 16,
    color: "#444",
  },
  footer: {
    borderTop: "1px solid #1a1a2a",
    padding: "20px 28px",
    display: "flex",
    justifyContent: "space-between",
    flexWrap: "wrap",
    gap: 12,
    fontSize: 10,
    color: "#666",
    letterSpacing: 1,
  },
};